import { useNavigate } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import type { getUserForm } from "../../lib/apis/dtos/request/user.request";
import { useUserList } from "../../lib/apis/hooks/user.hook";
import Button from "../../components/ui/button";
import {
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Table,
  ButtonGroup,
  Button as MuiButton,
} from "@mui/material";
import SearchInput from "../../components/ui/input/search.component";
import { useEnterKey } from "../../lib/hooks/useEnterKey";
import { formatDate } from "../../utils/date.utils";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import { useAuth } from "../../store/useAuthStore";
import { ROLE_TYPE_MENU, ROLES, type Role } from "../../types/auth.types";

export default function AccountPage() {
  const { openModal } = useModalStore();
  const auth = useAuth();

  // ROUTER
  const navigate = useNavigate();

  // STATE
  const [form, setForm] = React.useState<getUserForm>({
    page: 0,
    size: 10,
    search: "",
    role: "ALL",
  });

  const { data, refetch, isSuccess } = useQuery({ ...useUserList(form) });
  const { result } = data || {};
  const content = result?.content;
  const page = result?.page;

  // HANDLER
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setForm((state) => ({ ...state, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((state) => ({
      ...state,
      page: 0,
      size: parseInt(event.target.value, 10),
    }));
  };

  useEnterKey(refetch);

  return (
    <div className="p-6">
      <div className="flex items-center p-4">
        <SearchInput
          onChange={(e) =>
            setForm((state) => ({ ...state, search: e.target.value }))
          }
          label="계정 검색"
          placeholder="이름, 아이디로 검색"
          onClick={() => refetch()}
        />
        {auth.role === ROLES.ADMIN && (
          <>
            <div className="h-[32px] w-[0.5px] border-[0.5px] mx-[24px] " />
            <Button
              label="계정 생성"
              className="bg-[#ffffff] text-[14px] w-[120px] h-[36px] text-[#1976D2] rounded-[3px] border border-[#1976D280] whitespace-nowrap"
              onClick={() => openModal(MODAL_KEY.AccountCreate, {})}
            />
          </>
        )}
      </div>

      {auth.role === ROLES.ADMIN && (
        <div className="flex-row-between px-[14px] py-[8px]">
          <p className="text-[14px] font-medium">
            전체 {page?.totalElements || 0}개
          </p>
          <ButtonGroup
            className="mt-[24px]"
            // variant="contained"
            aria-label="account-group"
          >
            {ROLE_TYPE_MENU.map(({ label, value }, index) => (
              <MuiButton
                key={index}
                style={{
                  backgroundColor:
                    form.role === value ? "rgba(25, 118, 210, 0.5)" : undefined,
                }}
                variant={form.role == value ? "contained" : "outlined"}
                onClick={() =>
                  setForm((state) => ({ ...state, role: value as Role }))
                }
              >
                {label}
              </MuiButton>
            ))}
          </ButtonGroup>
        </div>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {auth.role === ROLES.ADMIN && <TableCell>권한</TableCell>}
              <TableCell>이름</TableCell>
              <TableCell>아이디</TableCell>
              <TableCell>연락처</TableCell>
              <TableCell>생성일</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSuccess &&
              (content || [])?.map(
                (
                  { userId, username, phone, createdAt, loginId, role },
                  index
                ) => (
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => navigate(`/account/${userId}`)}
                    key={`table_${index}`}
                  >
                    {auth.role === ROLES.ADMIN && <TableCell>{ROLES[role as Role]}</TableCell>}
                    <TableCell>{username}</TableCell>
                    <TableCell>{loginId}</TableCell>
                    <TableCell>{phone}</TableCell>
                    <TableCell>{formatDate(createdAt)}</TableCell>
                  </TableRow>
                )
              )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={page?.totalElements || 0}
        rowsPerPage={form.size}
        page={form.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}